import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAccountType,
  getRandomIntCrypto,
  getUserDetails,
} from '../../../utils';
import {
  communityHomeGoogleAdsKeyValues,
  GOOGLE_ADS_COMMUNITY_CHANNEL,
} from '../../../utils/adsUtils';
import {
  checkIsAuthenticated,
  getProfilePublicId,
  getUserPublicId,
} from '../../../utils/authUtils';
import { random_profile_pic } from '../../../utils/loveUtils';
import { COMMUNITY_HOME_META } from '../../../utils/meta';

// Dynamic imports for components
const ContainerWithAd = dynamic(
  () => import('../../components/Common/ContainerWithAd/ContainerWithAd')
);
const ForYouSection = dynamic(
  () => import('../../components/Common/ForYouSection/ForYouSection')
);
const CategorySectionBlock = dynamic(
  () =>
    import(
      '../../components/Community/CategorySectionBlock/CategorySectionBlock'
    ),
  {
    loading: () => <div className='min-h-[533px]'></div>,
  }
);
const CategorySectionContainer = dynamic(
  () =>
    import(
      '../../components/Community/CategorySectionContainer/CategorySectionContainer'
    ),
  {
    loading: () => <div className='min-h-[533px]'></div>,
  }
);
const FeaturedSection = dynamic(
  () => import('../../components/Community/FeaturedSection/FeaturedSection'),
  {
    loading: () => (
      <>
        <div className='h-[280px] w-full bg-light-grey'></div>
        <div className='-mt-[246px] mb-10'>
          <Skeleton className='md: mb-6 ml-6  h-[43px] w-[90%] max-w-[403px] md:w-[403px]' />
          <div className='flex flex-wrap gap-4 px-2'>
            <PostCardSkeleton />
            <PostCardSkeleton />
            <PostCardSkeleton />
          </div>
        </div>
      </>
    ),
  }
);
const LoveNotRegisteredCard = dynamic(
  () =>
    import(
      '../../components/Community/LoveNotRegisteredCard/LoveNotRegisteredCard'
    )
);
const LovePersonCard = dynamic(
  () => import('../../components/Community/LovePersonCard/LovePersonCard')
);
const LovePersonCardBlurred = dynamic(
  () =>
    import(
      '../../components/Community/LovePersonCardBlurred/LovePersonCardBlurred'
    )
);
const LoveTakeQuizCard = dynamic(
  () => import('../../components/Community/LoveTakeQuizCard/LoveTakeQuizCard')
);
const NoDataFound = dynamic(
  () => import('../../components/Community/NoDataFound/NoDataFound')
);
const SliderSection = dynamic(
  () => import('../../components/Community/SliderSection/SliderSection'),
  {
    loading: () => <div className='min-h-[230px]'></div>,
  }
);
const TagSection = dynamic(
  () => import('../../components/Community/TagSection/TagSection'),
  {
    loading: () => <div className='min-h-[76px]'></div>,
  }
);
const GoogleAdBannerHorizontal = dynamic(
  () => import('../../components/GoogleAd/GoogleAdBannerHorizontal')
);
const GoogleAdBannerTop = dynamic(
  () => import('../../components/GoogleAd/GoogleAdBannerTop')
);
const GoogleAdBannerVertical2 = dynamic(
  () => import('../../components/GoogleAd/GoogleAdBannerVertical2')
);
const EcommerceAds = dynamic(
  () => import('../../components/Market/MarketAdContainer/EcommerceAds'),
  {
    loading: () => <div className='min-h-[453px]'></div>,
  }
);
const PageContainer = dynamic(
  () => import('../../components/PageContainer/PageContainer')
);
// const CategorySectionSkeleton = dynamic(
//   () => import('../../components/SkeletonLoaders/CategorySectionSkeleton')
// );

import { Skeleton } from '../../components/Common/Skeleton';
import CategorySectionSkeleton from '../../components/SkeletonLoaders/CategorySectionSkeleton';
import PostCardSkeleton from '../../components/SkeletonLoaders/PostCardSkeleton';
import useMobileDetect from '../../hooks/useMobileDetect';
import { setHighlightItem } from '../../store/slices/common/commonSlice';
import {
  getFeaturedForumPostsAsync,
  getForumPostsAsync,
} from '../../store/slices/community/communitySlice';
import { getfollowingsAsync } from '../../store/slices/community/followSlice';
import {
  getCurrentUserRegistered,
  getPreferredUsers,
} from '../../store/slices/community/loveSlice';

export async function getServerSideProps({ query }) {
  const { city } = query;
  return {
    props: {
      metaData: COMMUNITY_HOME_META(city),
    },
  };
}

const Community = () => {
  const [isPostDeleted, setIsPostDeleted] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const { city } = router.query;
  const userDetails = getUserDetails();

  const [forumData, setForumData] = useState(null);
  const isMobile = useMobileDetect();

  const [featuredForumPosts, setFeaturedForumPosts] = useState(null);

  const isCurrentUserRegistered = useSelector(
    ({ love }) => love?.isCurrentUserRegistered
  );

  const preferred_users = useSelector(({ love }) => love?.preferredUsers);

  const isRegisteredInLove = () => {
    if (!isCurrentUserRegistered) return false;
    else if (isCurrentUserRegistered === 'Not Registered Yet') return false;
    else return true;
  };

  useEffect(() => {
    dispatch(getForumPostsAsync()).then((res) => {
      if (res?.meta?.requestStatus === 'fulfilled') {
        setForumData(res?.payload);
        setIsPostDeleted(false);
      }
    });
  }, [isPostDeleted, city, isMobile]);

  useEffect(() => {
    dispatch(getFeaturedForumPostsAsync())
      .then((res) => {
        if (res?.meta?.requestStatus === 'fulfilled') {
          setFeaturedForumPosts(res?.payload);
          setIsPostDeleted(false);
        }
      })
      .catch((err) => console.error('Something went wrong', err));
  }, [isPostDeleted, city]);

  useEffect(() => {
    if (userDetails?.profile_id)
      dispatch(getfollowingsAsync({ profile_id: userDetails?.profile_id }));
  }, [userDetails?.profile_id]);

  useEffect(() => {
    dispatch(setHighlightItem('Home'));
  }, []);

  useEffect(() => {
    if (
      userDetails &&
      isCurrentUserRegistered &&
      isCurrentUserRegistered !== 'Not Registered Yet'
    )
      dispatch(getPreferredUsers());
  }, [isCurrentUserRegistered]);

  useEffect(() => {
    if (userDetails)
      dispatch(
        getCurrentUserRegistered({
          user_id: getUserPublicId(),
          profile_id: getProfilePublicId(),
        })
      );
  }, []);

  return (
    <PageContainer showFooter={forumData?.length > 0}>
      <ContainerWithAd
        ad={
          <GoogleAdBannerVertical2
            channel={GOOGLE_ADS_COMMUNITY_CHANNEL}
            keyValues={communityHomeGoogleAdsKeyValues}
          />
        }
      >
        {isMobile ? (
          <div className='mt-[16px] flex justify-center'>
            <GoogleAdBannerHorizontal
              id='top'
              channel={GOOGLE_ADS_COMMUNITY_CHANNEL}
              keyValues={communityHomeGoogleAdsKeyValues}
              pos={1}
            />
          </div>
        ) : (
          <GoogleAdBannerTop
            channel={GOOGLE_ADS_COMMUNITY_CHANNEL}
            keyValues={communityHomeGoogleAdsKeyValues}
          />
        )}
        {forumData && forumData?.length > 0 ? (
          <TagSection />
        ) : (
          <div className='min-h-[76px]'></div>
        )}
        <FeaturedSection
          featuredForumPosts={featuredForumPosts}
          setIsPostDeleted={setIsPostDeleted}
        />
        <SliderSection />

        {isMobile && (
          <div className='mt-[32px] flex justify-center'>
            <GoogleAdBannerHorizontal
              id='adslot_horizontal_2'
              keyValues={communityHomeGoogleAdsKeyValues}
              channel={GOOGLE_ADS_COMMUNITY_CHANNEL}
              pos={2}
            />
          </div>
        )}

        {forumData ? (
          <div className='my-8'>
            {forumData.map((item, index) => (
              <div className='' key={index}>
                {isMobile &&
                  item?.id === 15 &&
                  preferred_users &&
                  preferred_users?.length > 0 && (
                    <div className='my-[30px] flex justify-center'>
                      <GoogleAdBannerHorizontal
                        id='adslot_horizontal_3'
                        keyValues={communityHomeGoogleAdsKeyValues}
                        channel={GOOGLE_ADS_COMMUNITY_CHANNEL}
                        pos={3}
                      />
                    </div>
                  )}
                {checkIsAuthenticated() &&
                item.id == 15 &&
                getAccountType() === 'Individual' ? (
                  <>
                    {/* use LoveCategorySection component below */}
                    {(() => {
                      if (!isRegisteredInLove()) {
                        return (
                          <CategorySectionContainer
                            category={{
                              id: forumData[3]?.id,
                              name: forumData[3]?.name,
                              isFollow: forumData[3]?.is_follow,
                            }}
                            link={{
                              pathname: '/community/categories/15',
                              query: {
                                city: city,
                              },
                            }}
                          >
                            <LoveNotRegisteredCard />
                            <LovePersonCardBlurred
                              profile_pic={
                                random_profile_pic[getRandomIntCrypto(5)]
                                  .profile_pic
                              }
                            />
                            <LovePersonCardBlurred
                              profile_pic={
                                random_profile_pic[getRandomIntCrypto(5)]
                                  .profile_pic
                              }
                            />
                          </CategorySectionContainer>
                        );
                      } else if (
                        isCurrentUserRegistered &&
                        isCurrentUserRegistered?.quiz_taken
                      ) {
                        return (
                          <CategorySectionContainer
                            category={{
                              id: forumData[3]?.id,
                              name: forumData[3]?.name,
                              isFollow: forumData[3]?.is_follow,
                            }}
                            link={{
                              pathname: '/community/categories/15',
                              query: {
                                city: city,
                              },
                            }}
                          >
                            {(() => {
                              if (
                                preferred_users &&
                                preferred_users?.length === 0
                              ) {
                                return <NoDataFound />;
                              } else {
                                /* This handles after login data fetching issue */
                                return (
                                  <>
                                    {preferred_users?.length &&
                                      Array.isArray(preferred_users) &&
                                      preferred_users
                                        ?.slice(0, 3)
                                        .map((item) => (
                                          <LovePersonCard
                                            key={item?.id}
                                            data={item}
                                          />
                                        ))}
                                  </>
                                );
                              }
                            })()}
                          </CategorySectionContainer>
                        );
                      } else if (
                        isCurrentUserRegistered &&
                        !isCurrentUserRegistered?.quiz_taken &&
                        getAccountType() === 'Individual'
                      ) {
                        return (
                          <CategorySectionContainer
                            category={{
                              id: forumData[3]?.id,
                              name: forumData[3]?.name,
                              isFollow: forumData[3]?.is_follow,
                            }}
                            link={{
                              pathname: '/community/categories/15',
                              query: {
                                city: city,
                              },
                            }}
                          >
                            <LoveTakeQuizCard />
                            {Array.isArray(preferred_users) &&
                              preferred_users
                                ?.slice(0, 2)
                                .map((item) => (
                                  <LovePersonCard key={item?.id} data={item} />
                                ))}
                          </CategorySectionContainer>
                        );
                      }
                    })()}
                    <EcommerceAds />
                  </>
                ) : (
                  <>
                    {item?.id === 14 &&
                      !isMobile &&
                      item?.posts?.length > 0 && (
                        <div className='my-[30px] flex justify-center'>
                          <GoogleAdBannerHorizontal
                            id='adslot_horizontal_4'
                            keyValues={communityHomeGoogleAdsKeyValues}
                            channel={GOOGLE_ADS_COMMUNITY_CHANNEL}
                            pos={4}
                          />
                        </div>
                      )}
                    <CategorySectionBlock
                      data={item}
                      setIsPostDeleted={setIsPostDeleted}
                    />

                    {item?.id < 13 && !isMobile && <EcommerceAds />}
                  </>
                )}
              </div>
            ))}
            {!isMobile && (
              <div className='my-[30px] flex justify-center'>
                <GoogleAdBannerHorizontal
                  id='adslot_horizontal_5'
                  keyValues={communityHomeGoogleAdsKeyValues}
                  channel={GOOGLE_ADS_COMMUNITY_CHANNEL}
                  pos={5}
                />
              </div>
            )}
            {checkIsAuthenticated() && <ForYouSection isHomePage />}
          </div>
        ) : (
          <div className='mt-6 flex flex-col gap-12'>
            {Array.from({ length: 7 }).map((_, index) => (
              <CategorySectionSkeleton key={index} />
            ))}
          </div>
        )}
      </ContainerWithAd>
    </PageContainer>
  );
};

export default Community;
